/**
 * The shared reference walkers (R11 extraction) — every condition tree and
 * every effect list in a pack runs through these two, whatever declares it
 * (an interaction, an event, a trigger, a description variant). Since D247
 * every effect list is reached from the schema's one roster of effect homes
 * ({@link checkEveryEffect}), never home by home.
 *
 * @remarks
 * Extracted from `semantic-validation.ts` when R11's condition/effect growth
 * pushed that file past the 500-line cap — the walkers were already one
 * self-contained idea. The `never` guards keep their D56 lesson: a bare
 * `switch` over a schema union is silently permissive, so every new variant
 * must break compilation HERE before it can ship unwalked.
 *
 * @packageDocumentation
 */

import type {
	ChooseEffect,
	ConditionalNarratives,
	ConditionDefinition,
	ContentPackDefinition,
	EffectDefinition,
	RecordHoldsCondition,
} from '@orpheus/content-schema';

import { brokenReference, requireDeclared } from './broken-reference.js';
import { type AmountCondition, checkAmountCondition, checkAmountEffect, isAmountCondition, isAmountEffect } from './counter-reference-walk.js';
import { type DeclaredIdSets } from './declared-ids.js';
import { effectSourcesOf } from './effect-sources.js';
import { checkItemCondition, isItemCondition, type ItemCondition } from './item-condition-walker.js';
import { type ReferenceBrokenFailure } from './load-failure.js';
import { checkRecordHoldsCondition } from './record-validation.js';
import { checkSentenceCondition } from './sentence-conditions.js';

// The declared-id universe lives in its own leaf (D226); every validator still reads it from here.
export { type DeclaredIdSets, declaredIdSetsOf } from './declared-ids.js';

/** R12: an occurrence-tally key is a family name, optionally narrowed after a colon (D235 added the answer family). */
const TALLY_KEY_PATTERN = /^(?:command_executed|command_rejected|dialogue_answered|parse_failed|reaction_spoken)(?::\S+)?$/u;

/**
 * Walk the conditions inside one (possibly conditional) authored text (D74).
 *
 * @param text - The authored text, plain or conditional
 * @param textPath - The path of the text field
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
export const checkNarrativeText = (
	text: string | { readonly variants: readonly { readonly condition: ConditionDefinition }[] },
	textPath: string,
	declared: DeclaredIdSets,
	failures: ReferenceBrokenFailure[],
): void => {
	if (typeof text === 'string') {
		return;
	}

	text.variants.forEach((variant, variantIndex) => {
		checkCondition(variant.condition, `${textPath}.variants[${String(variantIndex)}].condition`, declared, failures);
	});
};

/**
 * Walk a description's fragments (D140): each fragment's own condition, and
 * the conditions inside its conditional text.
 *
 * @param fragments - The authored fragments
 * @param fragmentsPath - The path of the fragments array
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
export const checkDescriptionFragments = (
	fragments: readonly { readonly text: string | { readonly variants: readonly { readonly condition: ConditionDefinition }[] }; readonly when: ConditionDefinition }[],
	fragmentsPath: string,
	declared: DeclaredIdSets,
	failures: ReferenceBrokenFailure[],
): void => {
	fragments.forEach((fragment, fragmentIndex) => {
		const fragmentPath = `${fragmentsPath}[${String(fragmentIndex)}]`;

		checkCondition(fragment.when, `${fragmentPath}.when`, declared, failures);
		checkNarrativeText(fragment.text, `${fragmentPath}.text`, declared, failures);
	});
};

/**
 * Walk every line of one narratives array through {@link checkNarrativeText} (D74).
 *
 * @param lines - The authored lines
 * @param linesPath - The path of the narratives array
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
export const checkNarrativeLines = (
	lines: undefined | readonly { readonly text: string | { readonly variants: readonly { readonly condition: ConditionDefinition }[] }}[],
	linesPath: string,
	declared: DeclaredIdSets,
	failures: ReferenceBrokenFailure[],
): void => {
	(lines ?? []).forEach((line, lineIndex) => {
		checkNarrativeText(line.text, `${linesPath}[${String(lineIndex)}].text`, declared, failures);
	});
};

/**
 * Walk a CONDITIONAL answer's every arm (D158) — the plain run of lines, or
 * the fallback and each variant's lines.
 *
 * @remarks
 * The variants' own `when` conditions are NOT walked here: this module's job
 * is the lines, and the conditions are checked where the owning shape knows
 * its path (`semantic-validation`'s exit walk). Keeping the two apart is why
 * `checkNarrativeLines` has stayed one function through five features.
 *
 * @param refusal - The authored answer, when there is one
 * @param refusalPath - Its path, for the failures
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
export const checkConditionalNarratives = (
	refusal: ConditionalNarratives | undefined,
	refusalPath: string,
	declared: DeclaredIdSets,
	failures: ReferenceBrokenFailure[],
): void => {
	if (refusal === undefined) {
		return;
	}

	if (!('variants' in refusal)) {
		checkNarrativeLines(refusal, refusalPath, declared, failures);

		return;
	}

	checkNarrativeLines(refusal.fallback, `${refusalPath}.fallback`, declared, failures);
	refusal.variants.forEach((variant, variantIndex) => {
		checkNarrativeLines(variant.narratives, `${refusalPath}.variants[${String(variantIndex)}].narratives`, declared, failures);
	});
};

/**
 * Walk a condition about one SUBJECT — an item's standing (the item-shaped
 * reads) or, D232/D233, an actor's whereabouts, or (D241) the Record — when
 * the condition is one of those.
 *
 * @param condition - The condition to walk
 * @param conditionPath - The path of `condition` itself
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 * @returns Whether the condition was one of the subject reads (and so is walked)
 */
const checkSubjectCondition = (
	condition: ConditionDefinition,
	conditionPath: string,
	declared: DeclaredIdSets,
	failures: ReferenceBrokenFailure[],
): condition is ActorCondition | AmountCondition | ItemCondition | RecordHoldsCondition => {
	// D241: the Record — read only where the pack keeps one, and only for what it remembers. Its own subject, walked
	// here (not in the switch below) at the complexity cap. D244: and the two AMOUNTS — a counter's, and the body's
	// health — each at a threshold it can reach, walked by the counter references.
	if (condition.type === 'record_holds') {
		checkRecordHoldsCondition(condition, conditionPath, declared.record, failures);

		return true;
	}

	if (isAmountCondition(condition)) {
		checkAmountCondition(condition, conditionPath, declared, failures);

		return true;
	}


	// The item-shaped reads live together, split out at the complexity cap.
	if (isItemCondition(condition)) {
		checkItemCondition(condition, conditionPath, declared, failures);

		return true;
	}

	// D232: the actor reads name a declared actor; whether it is ever on
	// stage is the producibility walk's question. D233: `actor_in_room` names
	// a declared room too. D234: whether a struck or fallen body is a FOE is
	// the producibility walk's question as well.
	if (isActorCondition(condition)) {
		requireDeclared(declared.actorIds, condition.actorId, `${conditionPath}.actorId`, 'condition names an undeclared actor', failures);

		if (condition.type === 'actor_in_room') {
			requireDeclared(declared.roomIds, condition.roomId, `${conditionPath}.roomId`, 'condition asks after an undeclared room', failures);
		}

		return true;
	}

	return false;
};

/** D232/D233/D234: the conditions that read one ACTOR — its whereabouts, and whether it has been struck or has fallen. */
type ActorCondition = Extract<
	ConditionDefinition,
	{ readonly type: 'actor_addressable' | 'actor_attacked' | 'actor_defeated' | 'actor_in_room' | 'actor_present' }
>;

/** The actor reads, as a roster, so the guard below cannot drift from the type. */
const ACTOR_CONDITION_TYPES: readonly string[] = ['actor_addressable', 'actor_attacked', 'actor_defeated', 'actor_in_room', 'actor_present'] satisfies readonly ActorCondition['type'][];

/** Whether a condition is one of the actor reads. */
const isActorCondition = (condition: ConditionDefinition): condition is ActorCondition => ACTOR_CONDITION_TYPES.includes(condition.type);

/**
 * Walk one condition tree, checking every id it names.
 *
 * @param condition - The condition to walk
 * @param conditionPath - The path of `condition` itself
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
export const checkCondition = (
	condition: ConditionDefinition,
	conditionPath: string,
	declared: DeclaredIdSets,
	failures: ReferenceBrokenFailure[],
): void => {
	if (checkSubjectCondition(condition, conditionPath, declared, failures)) {
		return;
	}

	switch (condition.type) {
		case 'all_of':
		case 'any_of':
		case 'at_least_of': {
			condition.conditions.forEach((nestedCondition, nestedIndex) => {
				checkCondition(nestedCondition, `${conditionPath}.conditions[${String(nestedIndex)}]`, declared, failures);
			});
			break;
		}
		// D219/D226/D229: the sentence's three slots. A named thing must be
		// declared, and the condition may only stand where a command is in
		// hand: a thing's answer to a verb it refuses, its authored verb's
		// arms, or a trigger listening to a command. Anywhere else it could
		// never hold, and a variant keyed on it would be dead on arrival.
		case 'direct_object_is':
		case 'indirect_object_is':
		case 'tool_is': {
			checkSentenceCondition(condition, conditionPath, declared, failures);
			break;
		}
		// R11: a NAMED ending must be declared; the any-ending form names nothing.
		case 'ending_selected': {
			if (condition.endingId !== undefined && !declared.endingIds.has(condition.endingId)) {
				failures.push(
					brokenReference(`${conditionPath}.endingId`, condition.endingId, 'condition names an undeclared ending'),
				);
			}
			break;
		}
		// R11: the fired-event predicate reads time's audit trail for a declared
		// event. D158's `event_pending` reads the queue instead, and takes the
		// same check — the self-reference species it CAN have is caught where
		// the owning event is known (see `checkEventPendingSelfReference`).
		case 'event_fired':
		case 'event_pending': {
			requireDeclared(declared.eventIds, condition.eventId, `${conditionPath}.eventId`, 'condition names an undeclared event', failures);
			break;
		}
		case 'flag_equals': {
			requireDeclared(declared.flagIds, condition.flagId, `${conditionPath}.flagId`, 'condition reads an undeclared flag', failures);
			break;
		}
		case 'log_read': {
			requireDeclared(declared.logIds, condition.logId, `${conditionPath}.logId`, 'condition names an undeclared log', failures);
			break;
		}
		case 'not': {
			checkCondition(condition.condition, `${conditionPath}.condition`, declared, failures);
			break;
		}
		// R12: tally keys are engine-minted — THIS check is the FAMILY prefix
		// only. The narrowed `:detail` halves are judged post-compile by R15's
		// occurrence-producibility table (membership in the discriminant lists
		// AND whether this pack can ever mint the key), because the lexicon
		// tier needs the merged vocabulary this step has not seen yet.
		case 'occurrence_tally_at_least': {
			if (!TALLY_KEY_PATTERN.test(condition.tallyKey)) {
				failures.push(
					brokenReference(
						`${conditionPath}.tallyKey`,
						condition.tallyKey,
						'tally keys start with an occurrence family: parse_failed, command_rejected, command_executed, dialogue_answered, or reaction_spoken',
					),
				);
			}
			break;
		}
		case 'room_equals': {
			requireDeclared(declared.roomIds, condition.roomId, `${conditionPath}.roomId`, 'condition names an undeclared room', failures);
			break;
		}
		case 'voice_line_spoken': {
			requireDeclared(
				declared.voiceLineIds,
				condition.voiceLineId,
				`${conditionPath}.voiceLineId`,
				'condition names an undeclared voice line',
				failures,
			);
			break;
		}
		default: {
			/*
			 * The `never` guard this switch was missing, and the reason it was
			 * missing an arm: D56 added `switched_on` to the union and nothing
			 * failed, because a bare `switch` over a discriminated union is
			 * silently permissive. An unwalked condition passes every load gate
			 * and then throws at describe time with a message blaming the loader
			 * — the loader that let it through.
			 *
			 * Every other switch over a schema union in this repo carries one
			 * (architecture.md §3.5); this was the gap.
			 */
			const unwalkedCondition: never = condition;

			throw new Error(`Unwalked condition variant: ${JSON.stringify(unwalkedCondition)}`);
		}
	}
};

/** The effects that name one thing and nothing else. */
type ItemEffect = Extract<EffectDefinition, { type: (typeof ITEM_EFFECT_TYPES)[number] }>;

const ITEM_EFFECT_TYPES = ['add_item_to_inventory', 'close_container', 'open_container', 'remove_item', 'switch_off', 'switch_on'] as const satisfies readonly EffectDefinition['type'][];

/**
 * Whether an effect names one thing and nothing else.
 *
 * @param effect - Any effect
 * @returns Whether it grants, removes, opens, closes or switches a thing
 */
const isItemEffect = (effect: EffectDefinition): effect is ItemEffect => (ITEM_EFFECT_TYPES as readonly string[]).includes(effect.type);

/**
 * D246: a branch names what its arms' `when`s name, each walked at the path
 * it stands at. D247: an arm's effects are an effect home of their own
 * (`effectHomesWithin`), so {@link checkEveryEffect} meets them there and
 * the branch does not walk them a second time.
 *
 * @param effect - The branch
 * @param effectPath - Its path
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
const checkBranch = (effect: ChooseEffect, effectPath: string, declared: DeclaredIdSets, failures: ReferenceBrokenFailure[]): void => {
	effect.arms.forEach((arm, armIndex) => {
		if (arm.when !== undefined) {
			checkCondition(arm.when, `${effectPath}.arms[${String(armIndex)}].when`, declared, failures);
		}
	});
};

/**
 * D247: check every id every effect in the pack names — every home the
 * schema's roster knows (`effectHomesOf`), every branch's arms among them.
 *
 * @remarks
 * The walk was written out by hand, home by home, in five validators —
 * interactions and verb arms, events, triggers, wear hooks, `onSpent`,
 * topics, a counter's edges, a stock's draws — and three homes were never
 * written at all: a body's gift arms (`giveInteractions`), its prompts'
 * options and its `onDefeat` (found while building D246). An undeclared flag
 * in a gift arm loaded, and failed only when the gift was given. The roster
 * exists so a rule cannot have that blind spot, and now the reference walk
 * reads it like every other "anything anywhere" rule does: a home the
 * schema grows is walked the day it is added.
 *
 * @param contentPack - The parsed pack
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
export const checkEveryEffect = (contentPack: ContentPackDefinition, declared: DeclaredIdSets, failures: ReferenceBrokenFailure[]): void => {
	for (const [homePath, effects] of effectSourcesOf(contentPack)) {
		effects.forEach((effect, effectIndex) => {
			checkEffect(effect, `${homePath}[${String(effectIndex)}]`, declared, failures);
		});
	}
};

/**
 * Check every id one effect names.
 *
 * @param effect - The effect to check
 * @param effectPath - The path of `effect` itself
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
export const checkEffect = (
	effect: EffectDefinition,
	effectPath: string,
	declared: DeclaredIdSets,
	failures: ReferenceBrokenFailure[],
): void => {
	// D244: the amounts — a counter moved, the body healed — walked by the counter references (the complexity cap).
	if (isAmountEffect(effect)) {
		checkAmountEffect(effect, effectPath, declared, failures);

		return;
	}

	// The effects that name one thing — granted, removed, its door or its switch
	// moved (D50/D56) — ask one question (split out at the complexity cap, D246).
	// Whether the thing HAS the door or the switch is `authoring-reachability`'s;
	// this is the plain dangling-reference question, so an undeclared id is
	// reported as what it is.
	if (isItemEffect(effect)) {
		requireDeclared(declared.itemIds, effect.itemId, `${effectPath}.itemId`, 'effect names an undeclared item', failures);

		return;
	}

	// D246: a branch names what its arms name.
	if (effect.type === 'choose') {
		checkBranch(effect, effectPath, declared, failures);

		return;
	}

	switch (effect.type) {
		case 'append_narrative': {
			// D74: the line's TEXT may be conditional, and its conditions
			// reference the world like any others.
			checkNarrativeText(effect.text, `${effectPath}.text`, declared, failures);
			break;
		}
		// D235: an ask names a body and a prompt; whether the prompt is THAT body's is the prompt layer's question.
		case 'ask_prompt': {
			requireDeclared(declared.actorIds, effect.actorId, `${effectPath}.actorId`, 'effect has an undeclared actor ask', failures);
			requireDeclared(declared.promptIds, effect.promptId, `${effectPath}.promptId`, 'effect asks a prompt no actor declares', failures);
			break;
		}
		// R9: the clock's hands name a declared event or they are dead on arrival.
		case 'cancel_event':
		case 'schedule_event': {
			requireDeclared(declared.eventIds, effect.eventId, `${effectPath}.eventId`, 'effect names an undeclared event', failures);
			break;
		}
		// D142: the photograph names nothing but, D270, the room it wakes the player in.
		case 'checkpoint': {
			if (effect.wakeRoomId !== undefined) {
				requireDeclared(declared.roomIds, effect.wakeRoomId, `${effectPath}.wakeRoomId`, 'effect wakes the player in an undeclared room', failures);
			}
			break;
		}
		// D233: a body is moved to a declared room, or offstage — which names nothing.
		case 'move_actor': {
			requireDeclared(declared.actorIds, effect.actorId, `${effectPath}.actorId`, 'effect moves an undeclared actor', failures);

			if (effect.destination !== 'offstage') {
				requireDeclared(declared.roomIds, effect.destination, `${effectPath}.destination`, 'effect moves the actor to an undeclared room', failures);
			}
			break;
		}
		case 'move_player': {
			requireDeclared(declared.roomIds, effect.targetRoomId, `${effectPath}.targetRoomId`, 'effect moves the player to an undeclared room', failures);
			break;
		}
		// R11: a conclusion must be declared before anything can select it.
		case 'set_ending': {
			requireDeclared(declared.endingIds, effect.endingId, `${effectPath}.endingId`, 'effect selects an undeclared ending', failures);
			break;
		}
		case 'set_flag': {
			requireDeclared(declared.flagIds, effect.flagId, `${effectPath}.flagId`, 'effect writes an undeclared flag', failures);
			break;
		}
		// R12: a speakable must be declared before anything can utter it.
		case 'speak_voice_line': {
			requireDeclared(
				declared.voiceLineIds,
				effect.voiceLineId,
				`${effectPath}.voiceLineId`,
				'effect speaks an undeclared voice line',
				failures,
			);
			break;
		}
		case 'unlock_exit': {
			requireDeclared(declared.exitIds, effect.exitId, `${effectPath}.exitId`, 'effect unlocks an undeclared exit', failures);
			break;
		}
		default: {
			// The same `never` guard the condition walker above was missing, for
			// the same reason: this switch had silently not grown a `switch_on`
			// arm, and nothing failed.
			const unwalkedEffect: never = effect;

			throw new Error(`Unwalked effect variant: ${JSON.stringify(unwalkedEffect)}`);
		}
	}
};

